import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, database } from '../firebase';
import { ref as dbRef, set, get } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';

const SubscriptionContext = createContext();

export const SUBSCRIPTION_TIERS = {
  LAUNCH_HUB: 'launch_hub', // Free tier
  VISION_HUB: 'vision_hub'  // Paid tier
};

// Launch Hub Limitations (Free Tier)
export const LAUNCH_HUB_LIMITS = {
  // Chat and Community
  maxChatRooms: 3,
  maxDirectMessages: 10, // per day
  maxCommunityPosts: 5, // per month
  
  // Content Creation
  maxTrailerSubmissions: 2, // per month
  maxFanHubPosts: 3, // per month
  
  // Highest Grossing Films
  canViewDetailedInfo: false, // Only basic info visible
  
  // Admin Features
  canAccessAdmin: false,
  
  // Storage and Media
  maxFileUploadSize: 5 * 1024 * 1024, // 5MB
  maxImagesPerPost: 2,
  
  // eDigest Newsletter
  canSubscribeToDigest: true, // Allow newsletter subscription
  
  // Partnership and Sponsorship
  canSubmitPartnerships: false,
  canSubmitSponsorships: false,
  canSubmitAdvertising: false
};

// Vision Hub Features (Unlimited)
export const VISION_HUB_FEATURES = {
  unlimited: true,
  canAccessAll: true
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscriptionTier, setSubscriptionTier] = useState(SUBSCRIPTION_TIERS.LAUNCH_HUB);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usageData, setUsageData] = useState({
    chatRoomsJoined: 0,
    directMessagesSent: 0,
    communityPosts: 0,
    trailerSubmissions: 0,
    fanHubPosts: 0,
    lastResetDate: new Date().toISOString().split('T')[0] // Today's date
  });

  // Initialize subscription data on auth state change
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        await loadUserSubscription(user.uid);
        await loadUsageData(user.uid);
      } else {
        setSubscriptionTier(SUBSCRIPTION_TIERS.LAUNCH_HUB);
        setSubscriptionData(null);
        setUsageData({
          chatRoomsJoined: 0,
          directMessagesSent: 0,
          communityPosts: 0,
          trailerSubmissions: 0,
          fanHubPosts: 0,
          lastResetDate: new Date().toISOString().split('T')[0]
        });
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const loadUserSubscription = async (userId) => {
    try {
      const subscriptionRef = dbRef(database, `users/${userId}/subscription`);
      const snapshot = await get(subscriptionRef);
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        setSubscriptionData(data);
        setSubscriptionTier(data.tier || SUBSCRIPTION_TIERS.LAUNCH_HUB);
      } else {
        // Default to Launch Hub for new users
        const defaultSubscription = {
          tier: SUBSCRIPTION_TIERS.LAUNCH_HUB,
          status: 'active',
          createdAt: new Date().toISOString()
        };
        await set(subscriptionRef, defaultSubscription);
        setSubscriptionData(defaultSubscription);
        setSubscriptionTier(SUBSCRIPTION_TIERS.LAUNCH_HUB);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
      setSubscriptionTier(SUBSCRIPTION_TIERS.LAUNCH_HUB);
    }
  };

  const loadUsageData = async (userId) => {
    try {
      const usageRef = dbRef(database, `users/${userId}/usage`);
      const snapshot = await get(usageRef);
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        // Reset usage if it's a new day/month
        const today = new Date().toISOString().split('T')[0];
        if (data.lastResetDate !== today) {
          const resetUsage = {
            ...data,
            directMessagesSent: 0, // Reset daily limits
            lastResetDate: today
          };
          await set(usageRef, resetUsage);
          setUsageData(resetUsage);
        } else {
          setUsageData(data);
        }
      } else {
        // Initialize usage data
        const initialUsage = {
          chatRoomsJoined: 0,
          directMessagesSent: 0,
          communityPosts: 0,
          trailerSubmissions: 0,
          fanHubPosts: 0,
          lastResetDate: new Date().toISOString().split('T')[0]
        };
        await set(usageRef, initialUsage);
        setUsageData(initialUsage);
      }
    } catch (error) {
      console.error('Error loading usage data:', error);
    }
  };

  const updateSubscription = async (newSubscriptionData) => {
    if (!user) return false;
    
    try {
      const subscriptionRef = dbRef(database, `users/${user.uid}/subscription`);
      await set(subscriptionRef, {
        ...newSubscriptionData,
        updatedAt: new Date().toISOString()
      });
      
      setSubscriptionData(newSubscriptionData);
      setSubscriptionTier(newSubscriptionData.tier);
      return true;
    } catch (error) {
      console.error('Error updating subscription:', error);
      return false;
    }
  };

  const incrementUsage = async (usageType) => {
    if (!user || subscriptionTier === SUBSCRIPTION_TIERS.VISION_HUB) {
      return true; // Vision Hub has unlimited usage
    }

    try {
      const newUsageData = {
        ...usageData,
        [usageType]: (usageData[usageType] || 0) + 1
      };
      
      const usageRef = dbRef(database, `users/${user.uid}/usage`);
      await set(usageRef, newUsageData);
      setUsageData(newUsageData);
      return true;
    } catch (error) {
      console.error('Error updating usage:', error);
      return false;
    }
  };

  const checkLimit = (limitType) => {
    if (subscriptionTier === SUBSCRIPTION_TIERS.VISION_HUB) {
      return { allowed: true, remaining: 'unlimited' };
    }

    const limits = LAUNCH_HUB_LIMITS;
    const current = usageData[limitType] || 0;
    
    switch (limitType) {
      case 'chatRoomsJoined':
        return {
          allowed: current < limits.maxChatRooms,
          remaining: Math.max(0, limits.maxChatRooms - current),
          limit: limits.maxChatRooms
        };
      case 'directMessagesSent':
        return {
          allowed: current < limits.maxDirectMessages,
          remaining: Math.max(0, limits.maxDirectMessages - current),
          limit: limits.maxDirectMessages
        };
      case 'communityPosts':
        return {
          allowed: current < limits.maxCommunityPosts,
          remaining: Math.max(0, limits.maxCommunityPosts - current),
          limit: limits.maxCommunityPosts
        };
      case 'trailerSubmissions':
        return {
          allowed: current < limits.maxTrailerSubmissions,
          remaining: Math.max(0, limits.maxTrailerSubmissions - current),
          limit: limits.maxTrailerSubmissions
        };
      case 'fanHubPosts':
        return {
          allowed: current < limits.maxFanHubPosts,
          remaining: Math.max(0, limits.maxFanHubPosts - current),
          limit: limits.maxFanHubPosts
        };
      default:
        return { allowed: true, remaining: 'unlimited' };
    }
  };

  const isVisionHub = () => subscriptionTier === SUBSCRIPTION_TIERS.VISION_HUB;
  const isLaunchHub = () => subscriptionTier === SUBSCRIPTION_TIERS.LAUNCH_HUB;

  const getFeatureAccess = (feature) => {
    if (isVisionHub()) return true;
    
    // Check Launch Hub specific restrictions
    switch (feature) {
      case 'detailedMovieInfo':
        return !LAUNCH_HUB_LIMITS.canViewDetailedInfo;
      case 'adminAccess':
        return LAUNCH_HUB_LIMITS.canAccessAdmin;
      case 'partnerships':
        return LAUNCH_HUB_LIMITS.canSubmitPartnerships;
      case 'sponsorships':
        return LAUNCH_HUB_LIMITS.canSubmitSponsorships;
      case 'advertising':
        return LAUNCH_HUB_LIMITS.canSubmitAdvertising;
      case 'digestSubscription':
        return LAUNCH_HUB_LIMITS.canSubscribeToDigest;
      default:
        return true;
    }
  };

  const value = {
    user,
    subscriptionTier,
    subscriptionData,
    usageData,
    loading,
    isVisionHub,
    isLaunchHub,
    updateSubscription,
    incrementUsage,
    checkLimit,
    getFeatureAccess,
    LAUNCH_HUB_LIMITS,
    VISION_HUB_FEATURES
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};